import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { ToastrModule } from 'ngx-toastr';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { CloudMessagingService } from './shared.service';
import { FitConfigProviderService } from './fit-config-provider.service';
import { UnauthorizedRequestInterceptor } from './unauthorized-request.interceptor';
import { NotificationsService } from './notifications.service';
import { AuthorizationNeededGuard } from './guards/authorization-needed.guard';
import { DateFormatPipe, DATE_FORMAT_PIPE_FORMATS, DateFormatSettings } from './pipes/date-format.pipe';
import { DateAgoPipe } from './pipes/date-ago.pipe';
import { MonthsAndDaysPipe } from '@app/pipes/months-and-days.pipe';
import { SecondsToTimestampPipe } from '@app/pipes/seconds-to-timestamp.pipe';
import { DurationMinutesPipe } from '@app/pipes/duration-minutes.pipe';
import { BytesFormatPipe } from './pipes/byte-format.pipe';
import { WeightKgPipe } from './pipes/weight-kg.pipe';
import { CounterComponent } from './components/counter/counter.component';
import { DocumentationLinkComponent } from './components/documentation-link/documentation-link.component';
import { LibraryModalComponent } from './components/library-modal/library-modal.component';
import { LibraryModalService } from './components/library-modal/library-modal.service';
import { AssignModalComponent } from './components/assign-modal/assign-modal.component';
import { AssignModalService } from './components/assign-modal/assign-modal.service';
import { AreaMarkerComponent } from './components/area-marker/area-marker.component';
import { RequiredDirective } from './directives/required.directive';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { StepperFixDirective } from '@app/iphone-fix-directives/iPhone-stepper-fix-directive';
import { VideoJsComponent } from './components/video-js/video-js.component';
import { ListSortComponent } from './components/list-sort/list-sort.component';
import { DropdownSortComponent } from './components/dropdown-sort/dropdown-sort.component';
import { OnboardingFixDirective } from './directives/onboarding.directive';
import { EmailNamePipe } from './pipes/email-name.pipe';
import { EmailNotificationRecipientPipe } from './pipes/email-notification-recipient.pipe';
import { TrimDeletedEmail } from './pipes/delete-email.pipe';
import { DecomposeObjectPipe } from './pipes/decompose-object.pipe';
import { TranslatePipe } from './translate.pipe';
import { CapitalizeFirstWordPipe } from '@app/pipes/capitalize-first-word.pipe';
import { CmsDrawerComponent } from './components/cms-drawer/cms-drawer.component';
import { CmsOverlayComponent } from './components/cms-overlay/cms-overlay.component';
import { CmsFilterDropdownComponent } from './components/cms-filter-dropdown/cms-filter-dropdown.component';
import { CmsOutlineIconComponent } from './components/cms-outline-icon/cms-outline-icon.component';
import { CmsNgSelectDirective, CmsNgSelectMenuSearchDirective, CmsNgSelectMultiDirective } from './directives/cms-ng-select.directive';
import { CmsRadioSelectOptionComponent } from './components/cms-radio-select/cms-radio-select-option.component';
import { TimeZoneService } from './timezone.service';
import { CurrencyService } from './currency.service';
import { DateUtilsService } from './date-utils.service';
import { ViewModeService } from './view-mode.service';

const dateFormatsFactory = (config: FitConfigProviderService): DateFormatSettings => {
    return {
        dateFormat: config.dateFormat,
        dateTimeFormat: config.dateTimeFormat,
        timeFormat: config.timeFormat
    };
};

@NgModule({
    declarations: [
        DateFormatPipe,
        StepperFixDirective,
        AssignModalComponent,
        CounterComponent,
        DocumentationLinkComponent,
        LibraryModalComponent,
        AreaMarkerComponent,
        RequiredDirective,
        DateAgoPipe,
        MonthsAndDaysPipe,
        SecondsToTimestampPipe,
        DurationMinutesPipe,
        BytesFormatPipe,
        WeightKgPipe,
        VideoJsComponent,
        ListSortComponent,
        DropdownSortComponent,
        OnboardingFixDirective,
        EmailNamePipe,
        EmailNotificationRecipientPipe,
        TrimDeletedEmail,
        DecomposeObjectPipe,
        TranslatePipe,
        CapitalizeFirstWordPipe,
        CmsDrawerComponent,
        CmsOverlayComponent,
        CmsFilterDropdownComponent,
        CmsOutlineIconComponent,
        CmsNgSelectDirective,
        CmsNgSelectMultiDirective,
        CmsNgSelectMenuSearchDirective,
        CmsRadioSelectOptionComponent,
    ],
    
    imports: [
        CommonModule,
        FormsModule,
        NgSelectModule,
        PaginationModule.forRoot(),
        ToastrModule.forRoot({
            timeOut: 2600,
            extendedTimeOut: 2600,
            iconClasses: {
                error: 'toast-custom-error',
                info: 'toast-custom-info',
                success: 'toast-custom-success',
                warning: 'toast-custom-warning'
            }
        }),
        ButtonsModule.forRoot()
    ],
    
    exports: [
        CommonModule,
        FormsModule,
        NgSelectModule,
        ToastrModule,
        ButtonsModule,
        PaginationModule,
        DateFormatPipe,
        StepperFixDirective,
        AssignModalComponent,
        CounterComponent,
        DocumentationLinkComponent,
        LibraryModalComponent,
        AreaMarkerComponent,
        RequiredDirective,
        DateAgoPipe,
        MonthsAndDaysPipe,
        SecondsToTimestampPipe,
        DurationMinutesPipe,
        BytesFormatPipe,
        WeightKgPipe,
        VideoJsComponent,
        ListSortComponent,
        DropdownSortComponent,
        OnboardingFixDirective,
        EmailNamePipe,
        EmailNotificationRecipientPipe,
        TrimDeletedEmail,
        DecomposeObjectPipe,
        TranslatePipe,
        CapitalizeFirstWordPipe,
        CmsDrawerComponent,
        CmsOverlayComponent,
        CmsFilterDropdownComponent,
        CmsOutlineIconComponent,
        CmsNgSelectDirective,
        CmsNgSelectMultiDirective,
        CmsNgSelectMenuSearchDirective,
        CmsRadioSelectOptionComponent,
    ],
    
    providers: [
        AuthorizationNeededGuard,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UnauthorizedRequestInterceptor,
            multi: true
        },
        NotificationsService,
        TimeZoneService,
        CurrencyService,
        AssignModalService,
        LibraryModalService,
        CloudMessagingService,
        DateUtilsService, // Add the new service
        ViewModeService,
        { provide: DATE_FORMAT_PIPE_FORMATS, useFactory: dateFormatsFactory, deps: [FitConfigProviderService] },
    ]
})

export class SharedModule { }
